/**
 * Report controls — the Assets-style column-header UI lifted out so any
 * report page can reuse it. This is the canonical home for the column /
 * filter / sort UI primitives; `CrudPage` consumes them too, so a behavior
 * change here applies system-wide.
 *
 * Quick start (hand-rolled report page):
 *
 *   const ctl = useReportControls({ namespace: '/employees', columns });
 *
 *   // in queryFn — combine with your own page/limit/search:
 *   const p = new URLSearchParams({ page, limit });
 *   for (const [k, v] of Object.entries(ctl.queryParams)) p.set(k, v);
 *
 *   // toolbar:
 *   <ColumnsToggle ctl={ctl} />
 *   <Button onClick={() => ctl.setDrawerOpen(true)}>Search</Button>
 *
 *   // table:
 *   <TableHeader><TableRow>
 *     {ctl.visibleColumns.map((c) => (
 *       <TableHead key={c.key} className="p-0">
 *         <ColumnHeader {...ctl.headerProps(c)} />
 *       </TableHead>
 *     ))}
 *   </TableRow></TableHeader>
 *
 *   {ctl.drawerOpen && <SearchDrawer {...ctl.drawerProps} />}
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Columns as ColumnsIcon, Search, X } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { usePreferences } from '@/hooks/use-preferences';
import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

export type ColumnFilterType = 'text' | 'number' | 'date' | 'boolean' | 'enum' | 'ref';

export interface ColumnConfig {
  key: string;
  label: string;
  render?: (row: any) => React.ReactNode;
  /** Per-column filter input. Omit to disable filtering on this column. */
  filterType?: ColumnFilterType;
  /** Server query parameter — defaults to `key` (with dotted segments dropped). */
  filterField?: string;
  /** Server-side sort field — defaults to `filterField` / `key`. Useful when
   *  the displayed column is a string but you want sort to use a derived
   *  numeric field (e.g. `fleetNumberSort` for `fleetNumber`). */
  sortField?: string;
  /** Choices for `filterType: 'enum'`. */
  enumOptions?: Array<{ value: string; label: string }>;
  /** For `filterType: 'ref'` — list endpoint to fetch picker options from. */
  refEndpoint?: string;
  /** Display field on the fetched option records (defaults to `name`). */
  refLabel?: string;
  /** Extra static query params for the ref list URL (e.g. `{ status: 'Active' }`). */
  refQuery?: Record<string, string>;
  /** When false (default true), the column is hidden out of the box. The user
   *  can still toggle it on via the Columns dropdown. */
  showByDefault?: boolean;
  /** How the server should format this column's value in CSV / PDF exports.
   *  When omitted it's inferred from `filterType` (number/boolean/date) and
   *  otherwise treated as text. Set explicitly when the inference is wrong —
   *  e.g. `'datetime'` for a date column whose cell shows a time, or
   *  `'image'` for a photo column (dropped from exports). */
  exportFormat?: 'text' | 'date' | 'datetime' | 'number' | 'boolean' | 'image';
  /** For `filterType: 'text'` — defaults to **true**. Renders a chip-style
   *  multi-value input: each `Enter` adds a chip and the filter combines
   *  them per the active operator (Contains → OR across chips; Not
   *  contains → row excluded if it contains ANY chip; Equals/Not equals →
   *  $in / $nin). Stored internally as `\n`-separated values; the API
   *  sees multiple `<field>_contains=<v>` (or `_ne` / `_ncontains`) query
   *  params. Set `filterMulti: false` to fall back to single-value input. */
  filterMulti?: boolean;
}

export type SortState = { field: string; order: 'asc' | 'desc'; group: boolean } | null;

// ============================================================================
// Utilities
// ============================================================================

/** Walk a dotted key path on a plain object (e.g. "asset.fleetNumber"). */
function getPath(obj: any, path: string): any {
  return path.split('.').reduce((a: any, k) => (a == null ? a : a[k]), obj);
}

/** Client-side filter for aggregated reports (where the endpoint can't accept
 *  the shell's URL params). Mirrors the server-side contract for each
 *  filterType so a row matches iff it would have matched the equivalent
 *  server filter. */
export function rowMatchesFilters(
  row: any,
  columns: ColumnConfig[],
  filters: Record<string, string>,
): boolean {
  for (const c of columns) {
    if (!c.filterType) continue;
    const field = c.filterField || c.key.split('.')[0];
    const cellValue = getPath(row, c.key) ?? getPath(row, field);
    if (c.filterType === 'text') {
      const v = (filters[`${field}_contains`] ?? '').trim().toLowerCase();
      if (v && !String(cellValue ?? '').toLowerCase().includes(v)) return false;
    } else if (c.filterType === 'enum' || c.filterType === 'ref') {
      const v = (filters[field] ?? '').trim();
      if (v && String(cellValue ?? '') !== v) return false;
    } else if (c.filterType === 'boolean') {
      const v = (filters[field] ?? '').trim();
      if (v === 'true' && cellValue !== true) return false;
      if (v === 'false' && cellValue !== false) return false;
    } else if (c.filterType === 'number') {
      const gte = filters[`${field}_gte`];
      const lte = filters[`${field}_lte`];
      const num = Number(cellValue);
      if (gte != null && gte !== '' && !(num >= Number(gte))) return false;
      if (lte != null && lte !== '' && !(num <= Number(lte))) return false;
    } else if (c.filterType === 'date') {
      const gte = filters[`${field}_gte`];
      const lte = filters[`${field}_lte`];
      const t = cellValue ? new Date(cellValue).getTime() : NaN;
      if (gte && !(t >= new Date(gte).getTime())) return false;
      if (lte && !(t <= new Date(lte).getTime())) return false;
    }
  }
  return true;
}

/** Client-side sort matching the shell's `sortState`. Stable; nulls last. */
export function applySort<T>(rows: T[], sortState: SortState): T[] {
  if (!sortState) return rows;
  const sign = sortState.order === 'asc' ? 1 : -1;
  return [...rows].sort((a: any, b: any) => {
    const av = getPath(a, sortState.field);
    const bv = getPath(b, sortState.field);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * sign;
    return String(av).localeCompare(String(bv)) * sign;
  });
}

/** Build the URL-param map from the active filters. Each `filterType` has
 *  its own server-side contract:
 *
 *    text   → `<field>_contains`             (case-insensitive substring)
 *    enum   → `<field>`                      (exact match)
 *    ref    → `<field>`                      (ObjectId)
 *    boolean→ `<field>` = "true" | "false"
 *    date   → `<field>_gte` / `<field>_lte`  (ISO date strings)
 *    number → `<field>_gte` / `<field>_lte`  (numeric)
 */
export function buildFilterParams(
  columns: ColumnConfig[],
  filters: Record<string, string>,
  colFilterField: (c: ColumnConfig) => string,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const c of columns) {
    if (!c.filterType) continue;
    const field = colFilterField(c);
    if (c.filterType === 'text') {
      const v = (filters[`${field}_contains`] ?? '').trim();
      if (v) out[`${field}_contains`] = v;
    } else if (c.filterType === 'enum' || c.filterType === 'boolean' || c.filterType === 'ref') {
      const v = (filters[field] ?? '').trim();
      if (v) out[field] = v;
    } else if (c.filterType === 'date' || c.filterType === 'number') {
      const gte = (filters[`${field}_gte`] ?? '').trim();
      const lte = (filters[`${field}_lte`] ?? '').trim();
      if (gte) out[`${field}_gte`] = gte;
      if (lte) out[`${field}_lte`] = lte;
    }
  }
  return out;
}

// ============================================================================
// Column header — clickable menu (Search / Sort / Group / Hide)
// ============================================================================
// Wraps the column label in a button that opens a DropdownMenu. The actions
// mirror Zoho's column-header dropdown: Search, Sort Ascending/Descending,
// Group Ascending/Descending, Hide Column. Sort/Group state lives one level
// up in the consumer; "Search" simply opens the right-side drawer.

export function ColumnHeader({
  column, field, sortField, sort, filters, onSort, onClearSort, onHide, onSearch,
}: {
  column: ColumnConfig;
  field: string;
  sortField: string;
  sort: { field: string; order: 'asc' | 'desc'; group: boolean } | null;
  filters: Record<string, string>;
  onSort: (order: 'asc' | 'desc', group: boolean) => void;
  onClearSort: () => void;
  onHide: () => void;
  onSearch: () => void;
}) {
  const isSorted = sort?.field === sortField;
  const sortGlyph = isSorted ? (sort!.order === 'asc' ? '↑' : '↓') : '';
  // Header dot is shown only when a REAL filter value is set on one of
  // this column's storage slots — skip `_op` (operator metadata) and
  // blank/empty values so changing the operator alone, or clearing all
  // input, removes the indicator.
  const hasFilter = Object.entries(filters).some(([k, v]) =>
    (k === field || k.startsWith(`${field}_`))
    && !k.endsWith('_op')
    && v != null
    && String(v).trim() !== '',
  );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="w-full px-3 py-2 text-left text-sm font-medium hover:bg-muted/50 inline-flex items-center gap-1"
        >
          <span>{column.label}</span>
          {sortGlyph && <span className="text-muted-foreground text-xs">{sortGlyph}</span>}
          {hasFilter && <span className="text-primary text-xs ml-0.5">●</span>}
          <ChevronDown className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        {column.filterType && (
          <DropdownMenuItem onSelect={onSearch}>
            <Search className="h-4 w-4 mr-2" /> Search
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={() => onSort('asc', false)}>
          Sort Ascending {isSorted && sort!.order === 'asc' && !sort!.group && '✓'}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onSort('desc', false)}>
          Sort Descending {isSorted && sort!.order === 'desc' && !sort!.group && '✓'}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onSort('asc', true)}>
          Group Ascending {isSorted && sort!.order === 'asc' && sort!.group && '✓'}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onSort('desc', true)}>
          Group Descending {isSorted && sort!.order === 'desc' && sort!.group && '✓'}
        </DropdownMenuItem>
        {isSorted && (
          <DropdownMenuItem onSelect={onClearSort} className="text-muted-foreground">
            Clear sort
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={onHide} className="text-destructive">
          Hide Column
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ============================================================================
// Right-side Search drawer
// ============================================================================
// Lists every filterable column. Each row expands inline to show that
// column's filter input — multiple rows can be open at once so the user can
// build a multi-column filter without drilling in/out. Rows that already
// have an active filter expand automatically when the drawer opens.

export function SearchDrawer({
  columns, filters, setFilters, fieldFor, focusedColumnKey, onClose,
}: {
  columns: ColumnConfig[];
  filters: Record<string, string>;
  setFilters: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  fieldFor: (c: ColumnConfig) => string;
  focusedColumnKey?: string | null;
  onClose: () => void;
}) {
  const [pickerSearch, setPickerSearch] = React.useState('');
  const filterable = React.useMemo(() => columns.filter((c) => !!c.filterType), [columns]);

  // Initial expand-set captures the rows that already have an active filter
  // when the drawer opens — so the user immediately sees what's set. The
  // focused column (clicked from its header's Search menu item) is always
  // expanded too, even if it has no active filter yet, so the user lands
  // straight on the input.
  const initialExpanded = React.useMemo(() => {
    const set = new Set<string>();
    for (const c of filterable) {
      const f = fieldFor(c);
      if (Object.keys(filters).some((k) => k === f || k.startsWith(`${f}_`))) {
        set.add(c.key);
      }
    }
    if (focusedColumnKey) set.add(focusedColumnKey);
    return set;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [expanded, setExpanded] = React.useState<Set<string>>(initialExpanded);

  // Scroll the focused column's row into view after mount AND focus its
  // first text-like input so the user can start typing immediately — no
  // need to click the input first. Tiny delay so the expand animation
  // runs first; otherwise the row's measured offset is for its collapsed
  // height and the scroll lands too high. The querySelector grabs the
  // first non-disabled input — works for text/number/date inputs and the
  // typeahead/multi-chip widgets (both wrap a plain <input>).
  const rowRefs = React.useRef<Record<string, HTMLLIElement | null>>({});
  React.useEffect(() => {
    if (!focusedColumnKey) return;
    const id = window.setTimeout(() => {
      const el = rowRefs.current[focusedColumnKey];
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const input = el.querySelector<HTMLInputElement>('input:not([disabled])');
      if (input) input.focus();
    }, 60);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggle(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  const visibleColumns = pickerSearch.trim()
    ? filterable.filter((c) => c.label.toLowerCase().includes(pickerSearch.trim().toLowerCase()))
    : filterable;

  // Active filter chips — one per (key, value) pair currently set.
  // The `_op` slot holds operator metadata (e.g. "contains" / "ncontains"
  // / "ne") that ColumnFilterInput uses to route the value into the right
  // storage key; it isn't itself a filter and must not surface as a chip
  // (otherwise the user sees a phantom `asset_op = contains` chip).
  const chips = Object.entries(filters)
    .filter(([k, v]) => v && String(v).trim() !== '' && !k.endsWith('_op'))
    .map(([key, value]) => {
      const baseField = key.replace(/_(contains|ncontains|ne|gte|lte)$/, '');
      const col = columns.find((c) => fieldFor(c) === baseField);
      return { key, value, label: col?.label ?? baseField, columnKey: col?.key, column: col };
    });

  return (
    <div className="fixed top-0 right-0 bottom-0 w-[400px] max-w-[92vw] bg-background border-l shadow-xl z-50 flex flex-col">
      <div className="flex items-center justify-between p-3 border-b">
        <div className="font-semibold text-sm">Search by column</div>
        <Button size="icon" variant="ghost" onClick={onClose}><X className="h-4 w-4" /></Button>
      </div>

      {chips.length > 0 && (
        <div className="px-3 py-2 border-b flex flex-wrap gap-1.5 items-center">
          <span className="text-xs text-muted-foreground">Active:</span>
          {chips.map((c) => (
            <button
              key={c.key}
              type="button"
              className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-xs px-2 py-0.5 hover:bg-primary/20"
              onClick={() => setFilters((p) => { const next = { ...p }; delete next[c.key]; return next; })}
              title="Clear this filter"
            >
              <span className="font-medium">{c.label}</span>
              <ChipValue column={c.column} rawValue={String(c.value)} />
              <X className="h-3 w-3" />
            </button>
          ))}
          <button
            type="button"
            className="ml-auto text-xs text-muted-foreground hover:text-foreground underline"
            onClick={() => setFilters({})}
          >Clear all</button>
        </div>
      )}

      <div className="p-3 border-b">
        <Input
          placeholder="Filter columns…"
          value={pickerSearch}
          onChange={(e) => setPickerSearch(e.target.value)}
          className="h-8"
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        <ul className="divide-y">
          {visibleColumns.length === 0 ? (
            <li className="p-3 text-sm text-muted-foreground">No filterable columns match.</li>
          ) : visibleColumns.map((c) => {
            const f = fieldFor(c);
            // "Filtered" only when an actual value is set on one of the
            // column's storage slots. Exclude `_op` (operator metadata —
            // changing the operator alone shouldn't flag the column) and
            // empty-string values (defensive — onChange already drops
            // them, but blank stamps from initialState would otherwise
            // trip the indicator).
            const hasActive = Object.entries(filters).some(([k, v]) =>
              (k === f || k.startsWith(`${f}_`))
              && !k.endsWith('_op')
              && v != null
              && String(v).trim() !== '',
            );
            const isOpen = expanded.has(c.key);
            return (
              <li key={c.key} ref={(el) => { rowRefs.current[c.key] = el; }}>
                <button
                  type="button"
                  onClick={() => toggle(c.key)}
                  className="w-full px-3 py-2.5 text-left text-sm hover:bg-muted/50 flex items-center justify-between"
                  aria-expanded={isOpen}
                >
                  <span className="flex items-center gap-2">
                    <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                    <span>{c.label}</span>
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {hasActive ? '● filtered' : c.filterType}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-3 pb-3 pt-1 bg-muted/20 space-y-1.5">
                    <div className="text-[11px] text-muted-foreground">
                      {filterTypeHint(c.filterType!)}
                    </div>
                    <ColumnFilterInput
                      column={c}
                      field={f}
                      filters={filters}
                      setFilters={setFilters}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function filterTypeHint(t: ColumnFilterType): string {
  switch (t) {
    case 'text':    return 'Substring match — case-insensitive.';
    case 'enum':    return 'Pick one value (or Any to clear).';
    case 'ref':     return 'Pick a referenced record (or Any to clear).';
    case 'boolean': return 'Yes / No / Any.';
    case 'date':    return 'Pick a date range. Leave a side blank for open-ended.';
    case 'number':  return 'Numeric range. Leave a side blank for open-ended.';
  }
}

// ============================================================================
// Active-filter chip — resolves ref filters from ObjectId → display label
// ============================================================================

/** Display the user-facing label for an active filter chip. Most filter
 *  types stash a human-readable value in the filter map (text contains,
 *  enum value, date range). The `ref` type stores the picked option's
 *  ObjectId, so we look up the matching record by re-reading the same ref
 *  list React Query has already cached and show its display field.
 *  Falls back to the raw value while options are still loading. */
function ChipValue({ column, rawValue }: { column?: ColumnConfig; rawValue: string }) {
  const trimmed = rawValue.length > 24 ? `${rawValue.slice(0, 24)}…` : rawValue;
  // Resolve ObjectId-shaped values as refs when the column declares a
  // refEndpoint. Two cases hit this path:
  //   • `filterType: 'ref'` — picker stores the picked ObjectId; always resolve.
  //   • `filterType: 'text'` on a ref field (e.g. JobCards Asset column,
  //      where the server uses refTextFilters to resolve typed substrings) —
  //      typed input stays as text (chip shows the typed string), but a
  //      deep-link like `?asset=<id>` puts a raw ObjectId in the chip slot.
  //      When the value matches the 24-hex pattern, look up the label.
  const looksLikeObjectId = /^[0-9a-f]{24}$/i.test(rawValue);
  const canResolveAsRef =
    !!column?.refEndpoint
    && (column.filterType === 'ref' || (column.filterType === 'text' && looksLikeObjectId));
  if (!canResolveAsRef) {
    return <span className="opacity-70">{trimmed}</span>;
  }
  return <RefChipLabel column={column!} id={rawValue} fallback={trimmed} />;
}

function RefChipLabel({ column, id, fallback }: { column: ColumnConfig; id: string; fallback: string }) {
  // Per-id GET against `${endpoint}/${id}` — works regardless of how many
  // total refs exist and even for refs excluded by the endpoint's list
  // baseFilter (e.g. Archived assets on /fleet/assets). The previous
  // implementation fetched `?limit=200` and searched the result, which
  // silently fell back to the raw ObjectId once the org grew past 200
  // refs of a given type. Query key matches RefSearchTypeahead's
  // single-id case so the cache is shared.
  const { data } = useQuery({
    queryKey: ['ref-by-ids', column.refEndpoint, id],
    queryFn: async () => {
      try {
        const r = await api<any>(`${column.refEndpoint}/${id}`);
        return r.success ? [r.data] : [];
      } catch { return []; }
    },
    staleTime: 5 * 60_000,
  });
  const refLabel = column.refLabel || 'name';
  const item = (data ?? [])[0];
  const label = item ? (getPath(item, refLabel) || (item as any).name || id) : fallback;
  return <span className="opacity-70">{String(label)}</span>;
}

// ============================================================================
// Per-column filter input
// ============================================================================
// Renders a tiny input matched to the column's `filterType`. The active
// value is held in the parent's `filters` state map under one of three key
// shapes:
//
//   text    → `<field>_contains`              (case-insensitive substring)
//   enum    → `<field>`                       (exact match)
//   ref     → `<field>`                       (picked option's ObjectId)
//   boolean → `<field>` = "true" | "false"
//   date    → `<field>_gte` / `<field>_lte`   (ISO date strings)
//   number  → `<field>_gte` / `<field>_lte`   (numeric)

export function ColumnFilterInput({
  column, field, filters, setFilters,
}: {
  column: ColumnConfig;
  field: string;
  filters: Record<string, string>;
  setFilters: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}) {
  if (!column.filterType) return null;
  const onChange = (k: string, v: string) =>
    setFilters((prev) => {
      const next = { ...prev };
      if (v === '') delete next[k]; else next[k] = v;
      return next;
    });
  const clearKey = (k: string) => onChange(k, '');

  if (column.filterType === 'text') {
    const opKey = `${field}_op`;
    const op = filters[opKey] || 'contains';
    // Storage key per operator — `contains` is the default and stays in the
    // legacy `<field>_contains` slot for backwards compat with deep-links.
    const keyForOp = (o: string) => (
      o === 'equals' ? field
      : o === 'ne' ? `${field}_ne`
      : o === 'ncontains' ? `${field}_ncontains`
      : `${field}_contains`
    );
    const activeKey = keyForOp(op);
    const v = filters[activeKey] ?? '';
    const setOp = (nextOp: string) => setFilters((prev) => {
      const next = { ...prev };
      // Pull whatever value is currently set (from any of the 4 slots) and
      // move it onto the new op's slot so toggling preserves typing.
      const curVal = (next[keyForOp(op)] ?? '').trim()
        || (next[`${field}_contains`] ?? '').trim()
        || (next[field] ?? '').trim();
      for (const slot of [`${field}_contains`, field, `${field}_ne`, `${field}_ncontains`]) delete next[slot];
      if (curVal) next[keyForOp(nextOp)] = curVal;
      if (nextOp === 'contains') delete next[opKey]; else next[opKey] = nextOp;
      return next;
    });
    if (column.filterMulti !== false) {
      // Multi-chip text inherits the same 4-operator vocabulary as the
      // single-value variant. Each chip is a value; the operator decides
      // how they combine:
      //   • Contains      → match rows where `field` contains ANY chip
      //                     (regex alternation: het|kim → either match)
      //   • Not contains  → match rows where `field` contains NONE of the chips
      //   • Equals        → match rows where `field` equals one of the chips ($in)
      //   • Not equals    → match rows where `field` equals none of the chips ($nin)
      return (
        <div className="flex gap-1">
          <Select value={op} onValueChange={setOp}>
            <SelectTrigger className="h-7 w-[110px] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="contains">Contains</SelectItem>
              <SelectItem value="ncontains">Not contains</SelectItem>
              <SelectItem value="equals">Equals</SelectItem>
              <SelectItem value="ne">Not equals</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex-1 min-w-0">
            <MultiChipFilterInput value={v} onChange={(nextV) => onChange(activeKey, nextV)} />
          </div>
        </div>
      );
    }
    return (
      <div className="flex gap-1">
        <Select value={op} onValueChange={setOp}>
          <SelectTrigger className="h-7 w-[110px] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="contains">Contains</SelectItem>
            <SelectItem value="ncontains">Not contains</SelectItem>
            <SelectItem value="equals">Equals</SelectItem>
            <SelectItem value="ne">Not equals</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-0">
          <Input
            value={v}
            onChange={(e) => onChange(activeKey, e.target.value)}
            placeholder={
              op === 'equals' ? 'Exact match…'
              : op === 'ne' ? 'Not exactly…'
              : op === 'ncontains' ? "Doesn't contain…"
              : 'Contains…'
            }
            className="h-7 text-xs pr-6"
          />
          {v && (
            <button
              type="button"
              onClick={() => clearKey(activeKey)}
              className="absolute right-1 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    );
  }

  if (column.filterType === 'enum') {
    const v = filters[field] ?? '';
    return (
      <Select value={v || '__any__'} onValueChange={(x) => onChange(field, x === '__any__' ? '' : x)}>
        <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Any" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__any__">Any</SelectItem>
          {(column.enumOptions ?? []).map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (column.filterType === 'ref') {
    // Storage slots, mirroring the text-filter layout so the server-side
    // refTextFilters handler resolves substrings against the label field:
    //   equals     → `field`              (picked ObjectId)
    //   ne         → `field_ne`           (picked ObjectId — server $ne)
    //   contains   → `field_contains`     (free-text substring on label)
    //   ncontains  → `field_ncontains`    (free-text not-contains on label)
    const opKey = `${field}_op`;
    const op = filters[opKey] || 'equals';
    const keyForOp = (o: string) => (
      o === 'equals' ? field
      : o === 'ne' ? `${field}_ne`
      : o === 'contains' ? `${field}_contains`
      : `${field}_ncontains`
    );
    const activeKey = keyForOp(op);
    const isTextOp = op === 'contains' || op === 'ncontains';

    const setOp = (nextOp: string) => setFilters((prev) => {
      const next = { ...prev };
      // Clear every slot for this field — the value semantics differ
      // between picker ops (id) and text ops (substring), so migration
      // is ambiguous. Cleaner to start fresh on a mode switch.
      for (const slot of [field, `${field}_ne`, `${field}_contains`, `${field}_ncontains`]) {
        delete next[slot];
      }
      if (nextOp === 'equals') delete next[opKey]; else next[opKey] = nextOp;
      return next;
    });

    return (
      <div className="flex gap-1">
        <Select value={op} onValueChange={setOp}>
          <SelectTrigger className="h-7 w-[110px] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="equals">Equals</SelectItem>
            <SelectItem value="ne">Not equals</SelectItem>
            <SelectItem value="contains">Contains</SelectItem>
            <SelectItem value="ncontains">Not contains</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1 min-w-0">
          {isTextOp ? (
            // Multi-chip free-text input — each Enter / blur adds a chip;
            // the server's refTextFilters resolves the substring(s) against
            // the labelField of the referenced model. Multiple chips combine
            // via regex alternation, e.g. "523|601" matches either fleet.
            <MultiChipFilterInput
              value={filters[activeKey] ?? ''}
              onChange={(nextV) => onChange(activeKey, nextV)}
            />
          ) : (
            <RefSearchTypeahead
              endpoint={column.refEndpoint!}
              refLabel={column.refLabel || 'name'}
              refQuery={column.refQuery}
              value={filters[activeKey] ?? ''}
              onChange={(v) => onChange(activeKey, v)}
            />
          )}
        </div>
      </div>
    );
  }

  if (column.filterType === 'boolean') {
    const v = filters[field] ?? '';
    return (
      <Select value={v || '__any__'} onValueChange={(x) => onChange(field, x === '__any__' ? '' : x)}>
        <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Any" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__any__">Any</SelectItem>
          <SelectItem value="true">Yes</SelectItem>
          <SelectItem value="false">No</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  if (column.filterType === 'date' || column.filterType === 'number') {
    const isDate = column.filterType === 'date';
    const inputType = isDate ? 'date' : 'number';
    const opKey = `${field}_op`;
    const op = filters[opKey] || 'between';
    const gteKey = `${field}_gte`;
    const lteKey = `${field}_lte`;
    const eqKey = field;
    const setOp = (nextOp: string) => setFilters((prev) => {
      const next = { ...prev };
      // Switching operators: clear variant keys that the new operator won't
      // read (e.g. moving away from "between" drops the unused bound).
      if (nextOp === 'between') {
        delete next[eqKey];
      } else if (nextOp === 'gt' || nextOp === 'after') {
        delete next[lteKey]; delete next[eqKey];
      } else if (nextOp === 'lt' || nextOp === 'before') {
        delete next[gteKey]; delete next[eqKey];
      } else if (nextOp === 'eq' || nextOp === 'on') {
        delete next[gteKey]; delete next[lteKey];
      }
      if (nextOp === 'between') delete next[opKey]; else next[opKey] = nextOp;
      return next;
    });
    const opOptions = isDate
      ? [['between', 'Between'], ['after', 'After'], ['before', 'Before'], ['on', 'On']]
      : [['between', 'Between'], ['gt', 'Greater than'], ['lt', 'Less than'], ['eq', 'Equals']];
    return (
      <div className="flex gap-1">
        <Select value={op} onValueChange={setOp}>
          <SelectTrigger className="h-7 w-[100px] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {opOptions.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        {op === 'between' && (
          <>
            <Input
              type={inputType}
              value={filters[gteKey] ?? ''}
              onChange={(e) => onChange(gteKey, e.target.value)}
              className="h-7 text-xs flex-1"
              placeholder="from"
            />
            <Input
              type={inputType}
              value={filters[lteKey] ?? ''}
              onChange={(e) => onChange(lteKey, e.target.value)}
              className="h-7 text-xs flex-1"
              placeholder="to"
            />
          </>
        )}
        {(op === 'gt' || op === 'after') && (
          <Input
            type={inputType}
            value={filters[gteKey] ?? ''}
            onChange={(e) => onChange(gteKey, e.target.value)}
            className="h-7 text-xs flex-1"
            placeholder={op === 'after' ? 'after' : 'min'}
          />
        )}
        {(op === 'lt' || op === 'before') && (
          <Input
            type={inputType}
            value={filters[lteKey] ?? ''}
            onChange={(e) => onChange(lteKey, e.target.value)}
            className="h-7 text-xs flex-1"
            placeholder={op === 'before' ? 'before' : 'max'}
          />
        )}
        {(op === 'eq' || op === 'on') && (
          <Input
            type={inputType}
            value={filters[eqKey] ?? ''}
            onChange={(e) => onChange(eqKey, e.target.value)}
            className="h-7 text-xs flex-1"
            placeholder={op === 'on' ? 'date' : 'exact'}
          />
        )}
      </div>
    );
  }

  return null;
}

/** Tiny ref-picker for `filterType: 'ref'`. Same UX as the enum dropdown but
 *  the options come from a list endpoint (e.g. `/master-data/departments`).
 *  The selected option's id is stored in the filter map as `<field>=<id>`. */
/**
 * Searchable, optionally multi-select ref filter. Combines a typeahead
 * (server-side `?search=<query>`) with a positioned results dropdown.
 *
 * Storage contract:
 *   - single mode → caller passes `value: string` (one id, or '')
 *   - multi  mode → caller passes `value: string` ('\n'-separated ids, or '')
 *
 * Labels for currently-selected ids that don't appear in the live search
 * results are fetched individually via `${endpoint}/${id}` so chips render
 * with proper names even when the user searches for something else.
 */
function RefSearchTypeahead({
  endpoint, refLabel, refQuery, value, onChange, multi,
}: {
  endpoint: string;
  refLabel: string;
  refQuery?: Record<string, string>;
  value: string;
  onChange: (v: string) => void;
  multi?: boolean;
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [query, setQuery] = React.useState('');
  const [debouncedQuery, setDebouncedQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 180);
    return () => clearTimeout(t);
  }, [query]);

  // Close on outside click
  React.useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const selectedIds = React.useMemo(
    () => (value ? value.split('\n').filter(Boolean) : []),
    [value],
  );

  const searchUrl = React.useMemo(() => {
    const params = new URLSearchParams({ limit: '50', ...(refQuery ?? {}) });
    if (debouncedQuery.trim()) params.set('search', debouncedQuery.trim());
    return `${endpoint}?${params.toString()}`;
  }, [endpoint, refQuery, debouncedQuery]);

  const { data: results = [] } = useQuery({
    queryKey: ['ref-search', searchUrl],
    queryFn: async () => (await api<any[]>(searchUrl)).data || [],
    enabled: open,
  });

  // Resolve labels for any selected id that isn't in the current search
  // results. Per-id GETs are cheap and cached by React Query.
  const { data: selectedItems = [] } = useQuery({
    queryKey: ['ref-by-ids', endpoint, selectedIds.join(',')],
    queryFn: async () => {
      if (selectedIds.length === 0) return [];
      const out = await Promise.all(selectedIds.map(async (id) => {
        try {
          const r = await api<any>(`${endpoint}/${id}`);
          return r.success ? r.data : null;
        } catch { return null; }
      }));
      return out.filter(Boolean) as any[];
    },
    enabled: selectedIds.length > 0,
    staleTime: 5 * 60_000,
  });
  const labelById = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const it of results) m.set(it.id, getPath(it, refLabel) || it.name || it.id);
    for (const it of selectedItems) m.set(it.id, getPath(it, refLabel) || it.name || it.id);
    return m;
  }, [results, selectedItems, refLabel]);

  const commit = (id: string) => {
    if (multi) {
      if (selectedIds.includes(id)) return;
      onChange([...selectedIds, id].join('\n'));
      setQuery('');
    } else {
      onChange(id);
      setQuery('');
      setOpen(false);
    }
  };
  const removeId = (id: string) => {
    if (multi) {
      onChange(selectedIds.filter((x) => x !== id).join('\n'));
    } else {
      onChange('');
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="flex flex-wrap items-center gap-1 min-h-[28px] px-1 py-0.5 rounded-md border bg-background focus-within:ring-1 focus-within:ring-ring">
        {multi && selectedIds.map((id) => (
          <span key={id} className="inline-flex items-center gap-1 rounded bg-accent text-accent-foreground text-[11px] px-1.5 py-0.5">
            {labelById.get(id) ?? id}
            <button type="button" onClick={() => removeId(id)} aria-label="Remove" className="opacity-60 hover:opacity-100">
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {!multi && selectedIds[0] && (
          <span className="inline-flex items-center gap-1 rounded bg-accent text-accent-foreground text-[11px] px-1.5 py-0.5">
            {labelById.get(selectedIds[0]) ?? selectedIds[0]}
            <button type="button" onClick={() => removeId(selectedIds[0])} aria-label="Clear" className="opacity-60 hover:opacity-100">
              <X className="h-3 w-3" />
            </button>
          </span>
        )}
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={selectedIds.length === 0 ? 'Search…' : (multi ? '' : '')}
          className="flex-1 min-w-[60px] text-xs bg-transparent outline-none border-0 px-1 py-0.5"
        />
      </div>
      {open && (
        <div className="absolute z-50 mt-1 left-0 right-0 max-h-60 overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md text-xs">
          {results.length === 0 && (
            <div className="px-3 py-2 text-muted-foreground">
              {debouncedQuery ? 'No matches' : 'Type to search…'}
            </div>
          )}
          {results.map((it: any) => {
            const label = getPath(it, refLabel) || it.name || it.id;
            const isSelected = selectedIds.includes(it.id);
            return (
              <button
                type="button"
                key={it.id}
                onClick={() => commit(it.id)}
                className={cn(
                  'block w-full text-left px-3 py-1.5 hover:bg-accent hover:text-accent-foreground',
                  isSelected && 'opacity-60',
                )}
              >
                {label}{isSelected ? ' ✓' : ''}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// useReportControls — bundles state + helpers + drop-in component props
// ============================================================================

interface UseReportControlsOptions {
  /** Stable id for this report — used as a key for the per-user preferences
   *  (column visibility / sort / filters all persist per-user under this). */
  namespace: string;
  columns: ColumnConfig[];
}

export interface ReportControls {
  columns: ColumnConfig[];
  visibleColumns: ColumnConfig[];
  visibleKeys: string[];
  setVisibleKeys: (next: string[]) => void;
  visibilityReady: boolean;
  defaultVisibleKeys: string[];
  filters: Record<string, string>;
  setFilters: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  sortState: SortState;
  setSortState: (next: SortState) => void;
  /** Computed query params — spread into your URLSearchParams. */
  queryParams: Record<string, string>;
  activeFilterCount: number;
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  colFilterField: (c: ColumnConfig) => string;
  colSortField: (c: ColumnConfig) => string;
  /** Pre-bound props for `<ColumnHeader>` — pass the column, get the rest. */
  headerProps: (c: ColumnConfig) => {
    column: ColumnConfig;
    field: string;
    sortField: string;
    sort: SortState;
    filters: Record<string, string>;
    onSort: (order: 'asc' | 'desc', group: boolean) => void;
    onClearSort: () => void;
    onHide: () => void;
    onSearch: () => void;
  };
  /** Pre-bound props for `<SearchDrawer>` — spread and the rest is wired. */
  drawerProps: {
    columns: ColumnConfig[];
    filters: Record<string, string>;
    setFilters: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    fieldFor: (c: ColumnConfig) => string;
    /** When the drawer was opened from a column-header "Search" click, this
     *  carries the originating column's key so the drawer can pre-expand +
     *  scroll that row into view. Null when opened from the toolbar. */
    focusedColumnKey: string | null;
    onClose: () => void;
  };
  /** Restore defaults (filters + sort + visibility). */
  resetView: () => void;
  hasNonDefaultView: boolean;
}

export function useReportControls({ namespace, columns }: UseReportControlsOptions): ReportControls {
  const defaultVisibleKeys = React.useMemo(
    () => columns.filter((c) => c.showByDefault !== false).map((c) => c.key),
    [columns],
  );
  const { value: visibleKeys, setValue: setVisibleKeysRaw, ready: visibilityReady } =
    usePreferences<string[]>(`report-cols:${namespace}`, defaultVisibleKeys);
  const visibleSet = React.useMemo(() => new Set(visibleKeys ?? defaultVisibleKeys), [visibleKeys, defaultVisibleKeys]);
  const visibleColumns = React.useMemo(() => columns.filter((c) => visibleSet.has(c.key)), [columns, visibleSet]);

  const { value: filtersRaw, setValue: setFiltersRaw } =
    usePreferences<Record<string, string>>(`report-filters:${namespace}`, {});
  const filters = filtersRaw ?? {};
  const setFilters: React.Dispatch<React.SetStateAction<Record<string, string>>> = React.useCallback((next) => {
    setFiltersRaw((prev) => (typeof next === 'function' ? (next as any)(prev ?? {}) : next));
  }, [setFiltersRaw]);

  const { value: sortState, setValue: setSortStateRaw } =
    usePreferences<SortState>(`report-sort:${namespace}`, null);

  const colFilterField = React.useCallback((c: ColumnConfig) => {
    if (c.filterField) return c.filterField;
    return c.key.split('.')[0];
  }, []);
  const colSortField = React.useCallback((c: ColumnConfig) => c.sortField ?? colFilterField(c), [colFilterField]);

  const filterParams = React.useMemo(
    () => buildFilterParams(columns, filters, colFilterField),
    [columns, filters, colFilterField],
  );

  const queryParams = React.useMemo(() => {
    const out: Record<string, string> = { ...filterParams };
    if (sortState) {
      out.sort = sortState.field;
      out.order = sortState.order;
    }
    return out;
  }, [filterParams, sortState]);

  const activeFilterCount = Object.keys(filterParams).length;

  const [drawerOpen, setDrawerOpen] = React.useState(false);
  // Column whose "Search" was clicked from the header dropdown — passed to
  // the drawer so it can open with that row already expanded and scrolled
  // into view. Null when the drawer is opened via the toolbar (no
  // particular column has focus).
  const [focusedColumnKey, setFocusedColumnKey] = React.useState<string | null>(null);

  const headerProps = React.useCallback((c: ColumnConfig) => ({
    column: c,
    field: colFilterField(c),
    sortField: colSortField(c),
    sort: sortState ?? null,
    filters,
    onSort: (order: 'asc' | 'desc', group: boolean) =>
      setSortStateRaw({ field: colSortField(c), order, group }),
    onClearSort: () => setSortStateRaw(null),
    onHide: () => setVisibleKeysRaw(columns.map((x) => x.key).filter((k) => k !== c.key && (visibleKeys ?? defaultVisibleKeys).includes(k))),
    onSearch: () => { setFocusedColumnKey(c.key); setDrawerOpen(true); },
  }), [colFilterField, colSortField, sortState, filters, setSortStateRaw, columns, visibleKeys, defaultVisibleKeys, setVisibleKeysRaw]);

  const drawerProps = {
    columns,
    filters,
    setFilters,
    fieldFor: colFilterField,
    focusedColumnKey,
    onClose: () => { setDrawerOpen(false); setFocusedColumnKey(null); },
  };

  const hasNonDefaultView =
    activeFilterCount > 0
    || sortState != null
    || (visibilityReady && visibleKeys != null && (
      visibleKeys.length !== defaultVisibleKeys.length
      || visibleKeys.some((k, i) => k !== defaultVisibleKeys[i])
    ));

  const resetView = React.useCallback(() => {
    setFiltersRaw({});
    setSortStateRaw(null);
    setVisibleKeysRaw(defaultVisibleKeys);
  }, [setFiltersRaw, setSortStateRaw, setVisibleKeysRaw, defaultVisibleKeys]);

  return {
    columns,
    visibleColumns,
    visibleKeys: visibleKeys ?? defaultVisibleKeys,
    setVisibleKeys: setVisibleKeysRaw,
    visibilityReady,
    defaultVisibleKeys,
    filters,
    setFilters,
    sortState: sortState ?? null,
    setSortState: setSortStateRaw,
    queryParams,
    activeFilterCount,
    drawerOpen,
    setDrawerOpen,
    colFilterField,
    colSortField,
    headerProps,
    drawerProps,
    resetView,
    hasNonDefaultView,
  };
}

/** Dropdown that toggles column visibility — drop into the report toolbar. */
export function ColumnsToggle({ ctl }: { ctl: ReportControls }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" disabled={!ctl.visibilityReady} className="hidden md:inline-flex">
          <ColumnsIcon className="h-4 w-4 mr-1" />
          Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
        {ctl.columns.map((c) => {
          const checked = ctl.visibleKeys.includes(c.key);
          return (
            <DropdownMenuItem
              key={c.key}
              onSelect={(e) => {
                e.preventDefault();
                ctl.setVisibleKeys(
                  checked
                    ? ctl.visibleKeys.filter((k) => k !== c.key)
                    : [...ctl.visibleKeys, c.key],
                );
              }}
            >
              <Checkbox checked={checked} className="mr-2 pointer-events-none" />
              {c.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Chip-style multi-value text filter. Used by columns that opt in via
 * `filterMulti: true`. Values stored as `\n`-separated string (single state
 * slot is much simpler than a parallel string[]); CrudPage expands to
 * multiple `<field>_contains=<v>` query params on the API call.
 *
 * Interactions:
 *   - Type + Enter (or comma) → commit current text as a new chip
 *   - Backspace on empty input → remove the last chip
 *   - Click X on a chip → remove that chip
 *   - Blur with pending text → auto-commit (so users who tab away don't lose typing)
 */
function MultiChipFilterInput({
  value, onChange,
}: { value: string; onChange: (next: string) => void }) {
  const chips = value ? value.split('\n').filter(Boolean) : [];
  const [draft, setDraft] = React.useState('');

  const commit = (raw: string) => {
    const t = raw.trim();
    if (!t) return;
    if (chips.includes(t)) { setDraft(''); return; }
    const next = [...chips, t].join('\n');
    onChange(next);
    setDraft('');
  };
  const removeAt = (i: number) => {
    const next = chips.filter((_, j) => j !== i).join('\n');
    onChange(next);
  };

  return (
    <div className="flex flex-wrap items-center gap-1 min-h-[28px] px-1 py-0.5 rounded-md border bg-background focus-within:ring-1 focus-within:ring-ring">
      {chips.map((c, i) => (
        <span
          key={`${c}-${i}`}
          className="inline-flex items-center gap-1 rounded bg-accent text-accent-foreground text-[11px] px-1.5 py-0.5"
        >
          {c}
          <button
            type="button"
            onClick={() => removeAt(i)}
            aria-label={`Remove ${c}`}
            className="opacity-60 hover:opacity-100"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            commit(draft);
          } else if (e.key === 'Backspace' && draft === '' && chips.length > 0) {
            e.preventDefault();
            removeAt(chips.length - 1);
          }
        }}
        onBlur={() => { if (draft) commit(draft); }}
        placeholder={chips.length === 0 ? 'Filter… (Enter to add)' : ''}
        className="flex-1 min-w-[60px] text-xs bg-transparent outline-none border-0 px-1 py-0.5"
      />
    </div>
  );
}
